<script>
  // Shared performance metric cards (U7) — lifted verbatim from BacktestResults.svelte. Consumed by
  // Backtester, Paper, and Live, fed the neutral result shape (metrics + equityCurve + trades). The metric
  // honesty guards (nullable Sharpe/Max-Drawdown render "—", the no-data guard suppresses confident zeros on
  // an empty curve) are PRESERVED exactly — they matter most on the live surface where "empty because broken"
  // must never read as "empty because correct".
  import { formatCurrency } from '../../lib/store.js';
  import { isResultMeaningful, formatSignedPercentValue } from '../../lib/strategyResult.js';
  import TradesTable from './TradesTable.svelte';

  // The neutral result as the API serializes it (camelCase). Byte-identical across Backtester/Paper/Live (U2).
  export let result = null;

  // When false, the no-data guard and the trades table are still shown but the unfilled banner is hidden — the
  // backtest path passes true; the live path has no window so unfilledWindows is always 0.
  export let showUnfilledBanner = true;

  // When false, the internal trades table is suppressed (the Overview tab shows Current Positions instead, with
  // the trades blotter on its own Trades tab — #5/U6). Defaults true so Backtester / Portfolio / Trades tab are
  // unchanged.
  export let showTradesTable = true;

  // The no-data guard copy. Defaults to the backtest wording so BacktestResults renders byte-identically; the
  // live views pass their own ("no NAV history yet") so the message reads correctly per mode.
  export let noDataTitle = 'No (or insufficient) market data for this selection.';
  export let noDataBody =
    "The run completed, but there isn't enough price history for the chosen symbol, window, and frequency to " +
    'produce meaningful results. Try a different symbol, a shorter/known window, or a coarser frequency.';

  $: metrics = result?.metrics ?? null;
  $: trades = result?.trades ?? [];
  $: meaningful = isResultMeaningful(result);

  // Metric honesty (R11/KTD7): Sharpe and Max Drawdown are nullable — render "—", never 0/NaN.
  $: sharpe = metrics?.sharpeRatio ?? null;

  function fmtRatio(v) {
    return v === null || v === undefined ? '—' : Number(v).toFixed(2);
  }
</script>

{#if result}
  {#if !meaningful}
    <!-- No-data guard (CRITICAL, KTD7/U5): suppress confident zeros when the equity curve is empty/flat. -->
    <div class="alert alert-warning bt-nodata">
      <strong>{noDataTitle}</strong>
      {noDataBody}
    </div>
  {:else}
    {#if showUnfilledBanner && result.unfilledWindows > 0}
      <!-- Honest unfilled-windows banner (backtest only — live has no window). -->
      <div class="alert alert-info bt-unfilled">
        Some windows in this range had no market data ({result.unfilledWindows} unfilled) — results cover
        only the filled portion.
      </div>
    {/if}

    <div class="bt-metrics">
      <div class="bt-metric-card primary">
        <span class="bt-metric-label">Net Return</span>
        <span class="bt-metric-value" class:pos={metrics.netReturns > 0} class:neg={metrics.netReturns < 0}>
          {metrics.netReturns > 0 ? '▲' : metrics.netReturns < 0 ? '▼' : ''}
          {formatSignedPercentValue(metrics.netReturns)}
        </span>
      </div>
      <div class="bt-metric-card primary">
        <span class="bt-metric-label">Max Drawdown</span>
        <span class="bt-metric-value neg">
          {metrics.maxDrawdown === null || metrics.maxDrawdown === undefined
            ? '—'
            : `-${Math.abs(metrics.maxDrawdown).toFixed(2)}%`}
        </span>
      </div>

      <div class="bt-metric-card">
        <span class="bt-metric-label">Sharpe Ratio</span>
        <span class="bt-metric-value">{fmtRatio(sharpe)}</span>
      </div>
      <div class="bt-metric-card">
        <span class="bt-metric-label">Total P&amp;L</span>
        <span class="bt-metric-value" class:pos={metrics.totalPnl > 0} class:neg={metrics.totalPnl < 0}>
          {formatCurrency(metrics.totalPnl)}
        </span>
        <span class="bt-metric-sub">
          Realized {formatCurrency(metrics.realizedPnl)} · Unrealized {formatCurrency(metrics.unrealizedPnl)}
        </span>
      </div>
      <div class="bt-metric-card context">
        <span class="bt-metric-label">NAV Index</span>
        <span class="bt-metric-value">{metrics.netAssetValue?.toFixed(4) ?? '—'}</span>
      </div>
    </div>

    {#if showTradesTable}
      <TradesTable {trades} />
    {/if}
  {/if}
{/if}

<style>
  @import '../Backtesting/BacktestResults.css';
</style>
